import React, { useState } from "react";
import { newRecordId } from "../services/api";
import {
  Link2,
  Plus,
  X,
  Edit2,
  Trash2,
  ExternalLink,
  Copy,
  Calendar,
  Bell,
  BellOff,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  CheckCircle2,
  Circle,
  Search,
} from "lucide-react";
import { formatDisplayDate, isPast } from "../utils/dateUtils";

// Normalize a user-entered URL so it is always an absolute link.
const normalizeUrl = (raw) => {
  const url = (raw || "").trim();
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
};

// Best-effort domain extraction for favicons / auto titles.
const getDomain = (raw) => {
  try {
    return new URL(normalizeUrl(raw)).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
};

const faviconFor = (raw) => {
  const domain = getDomain(raw);
  if (!domain) return "";
  return `https://www.google.com/s/2/favicons?sz=64&domain_url=${domain}`;
};

const newLink = () => ({
  id: Date.now() + Math.floor(Math.random() * 100000),
  url: "",
  description: "",
  completed: false,
});

const QuickLinks = ({
  cards = [],
  onAddCard,
  onUpdateCard,
  onDeleteCard,
  onDeleteAllCards,
  onToggleLink,
  onMovePanelUp,
  onMovePanelDown,
  isFirstPanel,
  isLastPanel,
}) => {
  const [showAddCard, setShowAddCard] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState("");

  // Draft state shared by the add form and the edit form.
  const [draftTitle, setDraftTitle] = useState("");
  const [draftDueDate, setDraftDueDate] = useState("");
  const [draftRemember, setDraftRemember] = useState(false);
  const [draftLinks, setDraftLinks] = useState([newLink()]);

  const resetDraft = () => {
    setDraftTitle("");
    setDraftDueDate("");
    setDraftRemember(false);
    setDraftLinks([newLink()]);
  };

  const openAddForm = () => {
    if (showAddCard) {
      setShowAddCard(false);
      resetDraft();
      return;
    }
    setEditingId(null);
    resetDraft();
    setShowAddCard(true);
  };

  const startEditing = (card) => {
    setShowAddCard(false);
    setEditingId(card.id);
    setDraftTitle(card.title || "");
    setDraftDueDate(card.dueDate || "");
    setDraftRemember(Boolean(card.remember));
    setDraftLinks(
      card.links && card.links.length
        ? card.links.map((l) => ({ ...l }))
        : [newLink()]
    );
  };

  const cancelForm = () => {
    setShowAddCard(false);
    setEditingId(null);
    resetDraft();
  };

  const updateDraftLink = (id, field, value) => {
    setDraftLinks((prev) =>
      prev.map((l) => {
        if (l.id !== id) return l;
        const updated = { ...l, [field]: value };
        return updated;
      })
    );
  };

  const addDraftLink = () => setDraftLinks((prev) => [...prev, newLink()]);

  const removeDraftLink = (id) =>
    setDraftLinks((prev) =>
      prev.length > 1 ? prev.filter((l) => l.id !== id) : prev
    );

  // Auto-suggest a title from the first valid link's domain when the title is empty.
  const suggestedTitle = () => {
    if (draftTitle.trim()) return draftTitle.trim();
    const firstWithUrl = draftLinks.find((l) => l.url.trim());
    if (firstWithUrl) {
      const domain = getDomain(firstWithUrl.url);
      if (domain) return domain;
    }
    return "";
  };

  const buildCardPayload = () => {
    const cleanedLinks = draftLinks
      .filter((l) => l.url.trim())
      .map((l) => ({
        id: l.id,
        url: normalizeUrl(l.url),
        description: (l.description || "").trim(),
        completed: Boolean(l.completed),
      }));
    return {
      title: suggestedTitle() || "Untitled",
      links: cleanedLinks,
      dueDate: draftDueDate || null,
      remember: draftRemember,
    };
  };

  const canSave = () =>
    draftLinks.some((l) => l.url.trim()) &&
    (draftTitle.trim() || draftLinks.some((l) => l.url.trim()));

  const handleAdd = () => {
    if (!canSave()) return;
    const payload = buildCardPayload();
    const allDone =
      payload.links.length > 0 && payload.links.every((l) => l.completed);
    onAddCard({
      id: newRecordId(),
      ...payload,
      completed: allDone,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    setShowAddCard(false);
    resetDraft();
  };

  const handleSaveEdit = () => {
    if (!canSave()) return;
    const payload = buildCardPayload();
    const allDone =
      payload.links.length > 0 && payload.links.every((l) => l.completed);
    onUpdateCard(editingId, {
      ...payload,
      completed: allDone,
      updatedAt: new Date().toISOString(),
    });
    cancelForm();
  };

  const toggleExpand = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Best-effort: open the first link in a fresh window, the rest as tabs.
  const openAllLinks = (card) => {
    const urls = (card.links || []).map((l) => l.url).filter(Boolean);
    if (urls.length === 0) return;
    const [first, ...rest] = urls;
    const win = window.open(first, "_blank", "noopener,noreferrer");
    rest.forEach((u) => window.open(u, "_blank", "noopener,noreferrer"));
    if (!win) {
      // Popup blocked - let the user know there's a fallback.
      alert(
        "Your browser blocked opening multiple tabs. Please allow popups for this site, or use the copy button on each link."
      );
    }
  };

  const matchesSearch = (card) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    if ((card.title || "").toLowerCase().includes(q)) return true;
    return (card.links || []).some(
      (l) =>
        (l.url || "").toLowerCase().includes(q) ||
        (l.description || "").toLowerCase().includes(q)
    );
  };

  const visibleCards = cards.filter(matchesSearch);
  const activeCards = visibleCards.filter((c) => !c.completed);
  const doneCards = visibleCards.filter((c) => c.completed);

  const linkProgress = (card) => {
    const total = (card.links || []).length;
    const done = (card.links || []).filter((l) => l.completed).length;
    return { total, done };
  };

  const taggable = (card) => Boolean(card.dueDate) && Boolean(card.remember);

  // ---- form (shared by add + edit) ----
  const renderForm = (mode) => (
    <div className="mb-4 p-4 bg-gray-700/50 rounded-xl border-2 border-cyan-500/50 space-y-3">
      <input
        type="text"
        value={draftTitle}
        onChange={(e) => setDraftTitle(e.target.value)}
        placeholder="Card title (auto-filled from the link if left blank)..."
        className="w-full bg-gray-700 text-cyan-200 px-3 py-2 rounded-lg border-2 border-gray-600 focus:border-cyan-500 outline-none"
        autoFocus
      />

      {/* Links editor */}
      <div className="space-y-2">
        {draftLinks.map((link, idx) => (
          <div
            key={link.id}
            className="p-3 bg-gray-800/60 rounded-lg border border-gray-600 space-y-2"
          >
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 flex-shrink-0">
                Link {idx + 1}
              </span>
              {link.url.trim() && (
                <img
                  src={faviconFor(link.url)}
                  alt=""
                  className="w-4 h-4 rounded-sm flex-shrink-0"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              )}
              {draftLinks.length > 1 && (
                <button
                  onClick={() => removeDraftLink(link.id)}
                  className="ml-auto text-gray-400 hover:text-red-400 transition-colors"
                  title="Remove this link"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <input
              type="text"
              value={link.url}
              onChange={(e) => updateDraftLink(link.id, "url", e.target.value)}
              placeholder="https://example.com"
              className="w-full bg-gray-700 text-cyan-200 px-3 py-2 rounded-lg border-2 border-gray-600 focus:border-cyan-500 outline-none text-sm"
            />
            <input
              type="text"
              value={link.description}
              onChange={(e) =>
                updateDraftLink(link.id, "description", e.target.value)
              }
              placeholder="Description (optional)"
              className="w-full bg-gray-700 text-cyan-200 px-3 py-2 rounded-lg border-2 border-gray-600 focus:border-cyan-500 outline-none text-sm"
            />
          </div>
        ))}
        <button
          onClick={addDraftLink}
          className="flex items-center gap-1 text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
        >
          <Plus className="w-4 h-4" /> Add another link
        </button>
      </div>

      {/* Due date + remember */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-cyan-400 flex-shrink-0" />
          <label className="text-sm text-cyan-300 flex-shrink-0">
            Due date (optional):
          </label>
          <input
            type="date"
            value={draftDueDate}
            onChange={(e) => setDraftDueDate(e.target.value)}
            className="bg-gray-700 text-cyan-200 px-3 py-1.5 rounded-xl border-2 border-gray-600 focus:border-cyan-500 outline-none text-sm"
          />
          {draftDueDate && (
            <button
              onClick={() => setDraftDueDate("")}
              className="text-xs text-gray-400 hover:text-cyan-300 transition-colors"
            >
              Clear
            </button>
          )}
        </div>
        <button
          onClick={() => setDraftRemember((v) => !v)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border-2 text-sm transition-colors ${
            draftRemember
              ? "bg-cyan-500/20 border-cyan-500/60 text-cyan-300"
              : "bg-gray-700 border-gray-600 text-gray-400 hover:text-cyan-300"
          }`}
          title="Toggle remember tag"
        >
          {draftRemember ? (
            <Bell className="w-4 h-4" />
          ) : (
            <BellOff className="w-4 h-4" />
          )}
          Remember
        </button>
      </div>
      {draftDueDate && !draftRemember && (
        <p className="text-xs text-gray-400">
          Tip: turn on "Remember" so this card shows in the Remember and
          Reminders panels.
        </p>
      )}

      <div className="flex gap-2">
        <button
          onClick={mode === "add" ? handleAdd : handleSaveEdit}
          disabled={!canSave()}
          className="flex-1 bg-cyan-500 hover:bg-cyan-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-2 px-4 rounded-lg transition-colors"
        >
          {mode === "add" ? "Add Card" : "Save Changes"}
        </button>
        <button
          onClick={cancelForm}
          className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  // ---- single card (view mode) ----
  const renderCard = (card) => {
    const isOpen = expanded[card.id];
    const { total, done } = linkProgress(card);
    const isThumbnail =
      total === 1 && (card.links[0].description || "").trim().length > 0;
    const overdue =
      card.dueDate && !card.completed && isPast(new Date(card.dueDate));
    const showTag = taggable(card);

    if (editingId === card.id) {
      return (
        <div
          key={card.id}
          className="bg-gray-700/50 rounded-xl border-2 border-cyan-500/50"
        >
          {renderForm("edit")}
        </div>
      );
    }

    return (
      <div
        key={card.id}
        className="bg-gray-700/50 rounded-xl border-2 border-gray-600 hover:border-cyan-500/50 transition-all overflow-hidden"
      >
        {/* Header row */}
        <div className="flex items-start gap-2 p-3">
          <button
            onClick={() => toggleExpand(card.id)}
            className="text-gray-400 hover:text-cyan-400 transition-colors mt-0.5 flex-shrink-0"
            title={isOpen ? "Collapse" : "Expand"}
          >
            {isOpen ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="text-cyan-300 font-semibold break-words">
                {card.title}
              </h4>
              {showTag && (
                <span
                  className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    card.completed
                      ? "bg-green-500/20 text-green-300"
                      : "bg-yellow-500/20 text-yellow-300"
                  }`}
                >
                  {card.completed ? "Completed" : "Incomplete"}
                </span>
              )}
            </div>
            {/* Meta badges */}
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <Link2 className="w-3 h-3" />
                {total} link{total === 1 ? "" : "s"}
              </span>
              {total > 0 && (
                <span className="text-xs text-gray-400">
                  {done}/{total} done
                </span>
              )}
              {card.dueDate && (
                <span
                  className={`text-xs flex items-center gap-1 ${
                    overdue ? "text-red-300" : "text-gray-400"
                  }`}
                >
                  <Calendar className="w-3 h-3" />
                  {formatDisplayDate(new Date(card.dueDate))}
                </span>
              )}
              {card.remember && (
                <span className="text-xs text-cyan-400 flex items-center gap-1">
                  <Bell className="w-3 h-3" />
                  Remember
                </span>
              )}
            </div>
          </div>

          {/* Card actions */}
          <div className="flex gap-1 flex-shrink-0">
            <button
              onClick={() => openAllLinks(card)}
              className="text-gray-400 hover:text-cyan-400 transition-colors p-1"
              title="Open all links (allow popups)"
            >
              <ExternalLink className="w-4 h-4" />
            </button>
            <button
              onClick={() => startEditing(card)}
              className="text-gray-400 hover:text-yellow-400 transition-colors p-1"
              title="Edit card"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                if (window.confirm(`Delete card "${card.title}"?`)) {
                  onDeleteCard(card.id);
                }
              }}
              className="text-gray-400 hover:text-red-400 transition-colors p-1"
              title="Delete card"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        {isOpen && (
          <div className="px-3 pb-3 pl-9">
            {isThumbnail ? (
              <ThumbnailLink
                link={card.links[0]}
                onToggle={() => onToggleLink(card.id, card.links[0].id)}
                onCopy={() => copyToClipboard(card.links[0].url)}
              />
            ) : (
              <div className="space-y-2">
                {card.links.map((link) => (
                  <LinkRow
                    key={link.id}
                    link={link}
                    onToggle={() => onToggleLink(card.id, link.id)}
                    onCopy={() => copyToClipboard(link.url)}
                  />
                ))}
              </div>
            )}

            {total > 1 && (
              <button
                onClick={() => openAllLinks(card)}
                className="mt-3 flex items-center gap-1.5 text-sm bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 px-3 py-1.5 rounded-lg transition-colors"
              >
                <ExternalLink className="w-4 h-4" /> Open all {total} links
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-gray-800 rounded-3xl p-4 sm:p-6 border-2 border-cyan-500/30 shadow-lg">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Link2 className="w-5 h-5 text-cyan-400 flex-shrink-0" />
          <h3 className="text-xl font-bold text-cyan-400">Quick Links</h3>
          <span className="text-xs text-gray-500">({cards.length})</span>
        </div>
        <div className="flex items-center gap-2">
          {(onMovePanelUp || onMovePanelDown) && (
            <div className="flex items-center gap-1">
              <button
                onClick={onMovePanelUp}
                disabled={isFirstPanel}
                className="p-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Move panel up"
              >
                <ChevronUp className="w-4 h-4 text-cyan-300" />
              </button>
              <button
                onClick={onMovePanelDown}
                disabled={isLastPanel}
                className="p-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Move panel down"
              >
                <ChevronDown className="w-4 h-4 text-cyan-300" />
              </button>
            </div>
          )}
          {cards.length > 0 && onDeleteAllCards && (
            <button
              onClick={() => {
                if (
                  window.confirm(
                    `Delete all ${cards.length} quick link cards? This cannot be undone.`
                  )
                ) {
                  onDeleteAllCards();
                }
              }}
              className="p-2 bg-red-500/20 hover:bg-red-500/40 border border-red-500/50 rounded-xl transition-colors"
              title="Delete all cards"
            >
              <Trash2 className="w-5 h-5 text-red-400" />
            </button>
          )}
          <button
            onClick={openAddForm}
            className="p-2 bg-cyan-500 hover:bg-cyan-600 rounded-xl transition-colors"
            title="Add new card"
          >
            {showAddCard ? (
              <X className="w-5 h-5 text-white" />
            ) : (
              <Plus className="w-5 h-5 text-white" />
            )}
          </button>
        </div>
      </div>

      {/* Search */}
      {cards.length > 0 && (
        <div className="relative mb-4">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search cards by title or link..."
            className="w-full bg-gray-700 text-cyan-200 pl-9 pr-3 py-2 rounded-lg border-2 border-gray-600 focus:border-cyan-500 outline-none text-sm"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-cyan-300"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {/* Add form */}
      {showAddCard && renderForm("add")}

      {/* Empty state */}
      {cards.length === 0 && !showAddCard && (
        <div className="text-center py-8 text-gray-400">
          <Link2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No quick links yet. Click the + button to add your first card!</p>
        </div>
      )}

      {cards.length > 0 && visibleCards.length === 0 && (
        <div className="text-center py-6 text-gray-400 text-sm">
          No cards match "{search}".
        </div>
      )}

      {/* Active cards */}
      {activeCards.length > 0 && (
        <div className="space-y-3">
          {activeCards.map(renderCard)}
        </div>
      )}

      {/* Done cards */}
      {doneCards.length > 0 && (
        <div className="mt-4 pt-4 border-t-2 border-gray-700">
          <div className="text-xs font-bold text-green-400 uppercase tracking-wider px-1 mb-3">
            Done ({doneCards.length})
          </div>
          <div className="space-y-3">{doneCards.map(renderCard)}</div>
        </div>
      )}
    </div>
  );
};

// A single link row with checkbox, hyperlink and copy button.
const LinkRow = ({ link, onToggle, onCopy }) => (
  <div className="flex items-center gap-2 bg-gray-800/60 p-2 rounded-lg group">
    <button onClick={onToggle} className="flex-shrink-0" title="Mark link done">
      {link.completed ? (
        <CheckCircle2 className="w-5 h-5 text-green-400" />
      ) : (
        <Circle className="w-5 h-5 text-cyan-400" />
      )}
    </button>
    <img
      src={faviconFor(link.url)}
      alt=""
      className="w-4 h-4 rounded-sm flex-shrink-0"
      onError={(e) => {
        e.currentTarget.style.display = "none";
      }}
    />
    <div className="flex-1 min-w-0">
      <a
        href={link.url}
        target="_blank"
        rel="noopener noreferrer"
        className={`text-sm break-all hover:underline ${
          link.completed ? "text-gray-400 line-through" : "text-cyan-300"
        }`}
      >
        {link.url}
      </a>
      {link.description && (
        <p className="text-xs text-gray-400 break-words">{link.description}</p>
      )}
    </div>
    <button
      onClick={onCopy}
      className="flex-shrink-0 text-gray-400 hover:text-cyan-400 transition-colors p-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
      title="Copy link"
    >
      <Copy className="w-4 h-4" />
    </button>
  </div>
);

// Richer "thumbnail" presentation for a single link + description card.
const ThumbnailLink = ({ link, onToggle, onCopy }) => (
  <div className="flex gap-3 bg-gray-800/60 p-3 rounded-lg border border-gray-600 group">
    <button onClick={onToggle} className="flex-shrink-0 mt-0.5" title="Mark link done">
      {link.completed ? (
        <CheckCircle2 className="w-5 h-5 text-green-400" />
      ) : (
        <Circle className="w-5 h-5 text-cyan-400" />
      )}
    </button>
    <div className="w-12 h-12 rounded-lg bg-gray-700 flex items-center justify-center flex-shrink-0 overflow-hidden">
      <img
        src={faviconFor(link.url)}
        alt=""
        className="w-8 h-8"
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-gray-400 uppercase tracking-wide">
        {getDomain(link.url)}
      </p>
      <a
        href={link.url}
        target="_blank"
        rel="noopener noreferrer"
        className={`text-sm font-medium break-all hover:underline ${
          link.completed ? "text-gray-400 line-through" : "text-cyan-300"
        }`}
      >
        {link.url}
      </a>
      {link.description && (
        <p className="text-sm text-gray-300 break-words mt-1">
          {link.description}
        </p>
      )}
    </div>
    <button
      onClick={onCopy}
      className="flex-shrink-0 text-gray-400 hover:text-cyan-400 transition-colors p-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
      title="Copy link"
    >
      <Copy className="w-4 h-4" />
    </button>
  </div>
);

export default QuickLinks;
